import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { addMonths, startOfMonth } from "date-fns";
import { ApiClientAxios, PendingChangeType } from "@repo/api";
import type { RootState } from "../rootStore";

type AllowedChangesItem = ApiClientAxios.AllowedChangesItem;
type GetAllowedChangesResponse = ApiClientAxios.GetAllowedChangesResponse;
type PensionPendingChange = ApiClientAxios.PensionPendingChange;

export interface AllowedChangesState {
	/** Original data from the API, keyed by contractId */
	data: Record<string, AllowedChangesItem>;
	/** Dev tool overrides, keyed by contractId → partial flag overrides */
	overrides: Record<string, Partial<Record<AllowedChangesBooleanKey, boolean>>>;
	/** Whether the initial API load has completed */
	loaded: boolean;
	/** Whether a fetch is currently in progress */
	loading: boolean;
	/** Error message if the last fetch failed */
	error: string | null;
	/** Global flags from the response */
	isGlobalContactAddressChangeEnabled: boolean;
	hasAnyFailedChange: boolean;
}

const initialState: AllowedChangesState = {
	data: {},
	overrides: {},
	loaded: false,
	loading: false,
	error: null,
	isGlobalContactAddressChangeEnabled: false,
	hasAnyFailedChange: false,
};

export enum AllowedChangesBooleanKey {
	CanChangeEmployerOrClientContribution = "canChangeEmployerOrClientContribution",
	CanChangeInvestmentStrategy = "canChangeInvestmentStrategy",
	CanChangeTaxOptimum = "canChangeTaxOptimum",
	CanChangeAmlIdentification = "canChangeAmlIdentification",
	CanChangeContactAddress = "canChangeContactAddress",
	CanChangeElectronicContacts = "canChangeElectronicContacts",
	CanChangeIsEmployerContributing = "canChangeIsEmployerContributing",
	CanChengeInflationProgram = "canChengeInflationProgram",
	CanChangeBeneficiaries = "canChangeBeneficiaries",
	IsClientIdentified = "isClientIdentified",
	IsBankIdIdentificationRequired = "isBankIdIdentificationRequired",
	IsAmlFulfilled = "isAmlFulfilled",
	HasAnyFailedChanges = "hasAnyFailedChanges",
	IsElectronicContactsChangeInProgress = "isElectronicContactsChangeInProgress",
	IsContactAddressChangeInProgress = "isContactAddressChangeInProgress",
}

export const createContributionPendingChange = ({
	monthlyContribution,
	doesEmployerContribute,
}: {
	monthlyContribution: number;
	doesEmployerContribute: boolean;
}): PensionPendingChange => {
	const createdDate = new Date();
	const validFromDate = startOfMonth(addMonths(createdDate, 1));

	return {
		createdDate: createdDate.toISOString(),
		validFromDate: validFromDate.toISOString(),
		changeType: PendingChangeType.ChangeContribution,
		detail: {
			newClientContribution: monthlyContribution,
			willEmployerContribute: doesEmployerContribute,
		},
	};
};

const setContractDataFlag = (
	state: AllowedChangesState,
	contractId: string,
	flag: AllowedChangesBooleanKey,
	value: boolean,
) => {
	const contract = state.data[contractId];
	if (!contract) {
		return;
	}

	(contract as unknown as Partial<Record<AllowedChangesBooleanKey, boolean>>)[flag] = value;
};

const setContractPendingChangesValue = (
	state: AllowedChangesState,
	contractId: string,
	pendingChanges: PensionPendingChange[] | null | undefined,
) => {
	const contract = state.data[contractId];
	if (!contract) {
		return;
	}

	contract.pendingChanges = pendingChanges;
};

const upsertContributionPendingChangeValue = (
	existingPendingChanges: PensionPendingChange[] | null | undefined,
	pendingChange: PensionPendingChange,
) => {
	const pendingChanges = existingPendingChanges ?? [];

	if (pendingChange.changeType !== PendingChangeType.ChangeContribution) {
		return pendingChanges;
	}

	const withoutContributionChange = pendingChanges.filter(
		(change) => change.changeType !== PendingChangeType.ChangeContribution,
	);

	return [pendingChange, ...withoutContributionChange];
};

const setOverrideFlagValue = (
	state: AllowedChangesState,
	contractId: string,
	flag: AllowedChangesBooleanKey,
	value: boolean,
) => {
	if (!state.overrides[contractId]) {
		state.overrides[contractId] = {};
	}

	state.overrides[contractId][flag] = value;
};

export const allowedChangesSlice = createSlice({
	name: "allowedChanges",
	initialState,
	reducers: {
		/** Trigger the saga to fetch allowedChanges from API */
		fetchAllowedChanges: (state, _action: PayloadAction<{ force?: boolean } | undefined>) => {
			state.loading = true;
			state.error = null;
		},

		/** Called by saga when the API call fails */
		fetchAllowedChangesFailed: (state, action: PayloadAction<string>) => {
			state.loading = false;
			state.error = action.payload;
		},

		/** Store the full API response into the slice */
		setAllowedChanges: (state, action: PayloadAction<GetAllowedChangesResponse>) => {
			const { contracts, isGlobalContactAddressChangeEnabled, hasAnyFailedChange } =
				action.payload;
			state.data = {};
			for (const contract of contracts) {
				state.data[contract.contractId] = contract;
			}
			state.isGlobalContactAddressChangeEnabled = isGlobalContactAddressChangeEnabled;
			state.hasAnyFailedChange = hasAnyFailedChange;
			state.loaded = true;
			state.loading = false;
			state.error = null;
		},

		setCanChangeEmployerOrClientContribution: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeEmployerOrClientContribution, action.payload.value);
		},
		setAllowedChangesCanChangeInvestmentStrategy: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeInvestmentStrategy, action.payload.value);
		},
		setCanChangeTaxOptimum: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeTaxOptimum, action.payload.value);
		},
		setCanChangeAmlIdentification: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeAmlIdentification, action.payload.value);
		},
		setCanChangeContactAddress: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeContactAddress, action.payload.value);
		},
		setCanChangeElectronicContacts: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeElectronicContacts, action.payload.value);
		},
		setCanChangeIsEmployerContributing: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeIsEmployerContributing, action.payload.value);
		},
		setCanChengeInflationProgram: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChengeInflationProgram, action.payload.value);
		},
		setCanChangeBeneficiaries: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeBeneficiaries, action.payload.value);
		},
		setIsClientIdentified: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.IsClientIdentified, action.payload.value);
		},
		setIsAmlFulfilled: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.IsAmlFulfilled, action.payload.value);
		},
		setHasAnyFailedChanges: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.HasAnyFailedChanges, action.payload.value);
		},
		setIsElectronicContactsChangeInProgress: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.IsElectronicContactsChangeInProgress, action.payload.value);
		},
		setIsContactAddressChangeInProgress: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setContractDataFlag(state, action.payload.contractId, AllowedChangesBooleanKey.IsContactAddressChangeInProgress, action.payload.value);
		},
		setContractPendingChanges: (
			state,
			action: PayloadAction<{ contractId: string; pendingChanges: PensionPendingChange[] | null | undefined }>,
		) => {
			setContractPendingChangesValue(state, action.payload.contractId, action.payload.pendingChanges);
		},
		upsertContributionPendingChange: (
			state,
			action: PayloadAction<{ contractId: string; pendingChange: PensionPendingChange }>,
		) => {
			const contract = state.data[action.payload.contractId];
			if (!contract) {
				return;
			}

			contract.pendingChanges = upsertContributionPendingChangeValue(
				contract.pendingChanges,
				action.payload.pendingChange,
			);
		},

		setOverrideCanChangeEmployerOrClientContribution: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeEmployerOrClientContribution, action.payload.value);
		},
		setOverrideCanChangeInvestmentStrategy: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeInvestmentStrategy, action.payload.value);
		},
		setOverrideCanChangeTaxOptimum: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeTaxOptimum, action.payload.value);
		},
		setOverrideCanChangeAmlIdentification: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeAmlIdentification, action.payload.value);
		},
		setOverrideCanChangeContactAddress: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeContactAddress, action.payload.value);
		},
		setOverrideCanChangeElectronicContacts: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeElectronicContacts, action.payload.value);
		},
		setOverrideCanChangeIsEmployerContributing: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeIsEmployerContributing, action.payload.value);
		},
		setOverrideCanChengeInflationProgram: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChengeInflationProgram, action.payload.value);
		},
		setOverrideCanChangeBeneficiaries: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.CanChangeBeneficiaries, action.payload.value);
		},
		setOverrideIsClientIdentified: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.IsClientIdentified, action.payload.value);
		},
		setOverrideIsBankIdIdentificationRequired: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.IsBankIdIdentificationRequired, action.payload.value);
		},
		setOverrideIsAmlFulfilled: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.IsAmlFulfilled, action.payload.value);
		},
		setOverrideHasAnyFailedChanges: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.HasAnyFailedChanges, action.payload.value);
		},
		setOverrideIsElectronicContactsChangeInProgress: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.IsElectronicContactsChangeInProgress, action.payload.value);
		},
		setOverrideIsContactAddressChangeInProgress: (state, action: PayloadAction<{ contractId: string; value: boolean }>) => {
			setOverrideFlagValue(state, action.payload.contractId, AllowedChangesBooleanKey.IsContactAddressChangeInProgress, action.payload.value);
		},

		/** Clear all overrides for a specific contract */
		resetContractOverrideFlag: (
			state,
			action: PayloadAction<{ contractId: string; flag: AllowedChangesBooleanKey }>,
		) => {
			const contractOverrides = state.overrides[action.payload.contractId];
			if (!contractOverrides) {
				return;
			}

			delete contractOverrides[action.payload.flag];

			if (Object.keys(contractOverrides).length === 0) {
				delete state.overrides[action.payload.contractId];
			}
		},

		/** Clear all overrides for a specific contract */
		resetContractOverrides: (state, action: PayloadAction<string>) => {
			delete state.overrides[action.payload];
		},

		/** Clear all overrides */
		resetAllOverrides: (state) => {
			state.overrides = {};
		},
	},
});

// --- Actions ---
export const {
	fetchAllowedChanges,
	fetchAllowedChangesFailed,
	setAllowedChanges,
	setCanChangeEmployerOrClientContribution,
	setAllowedChangesCanChangeInvestmentStrategy,
	setCanChangeTaxOptimum,
	setCanChangeAmlIdentification,
	setCanChangeContactAddress,
	setCanChangeElectronicContacts,
	setCanChangeIsEmployerContributing,
	setCanChengeInflationProgram,
	setCanChangeBeneficiaries,
	setIsClientIdentified,
	setIsAmlFulfilled,
	setHasAnyFailedChanges,
	setIsElectronicContactsChangeInProgress,
	setIsContactAddressChangeInProgress,
	setContractPendingChanges,
	upsertContributionPendingChange,
	setOverrideCanChangeEmployerOrClientContribution,
	setOverrideCanChangeInvestmentStrategy,
	setOverrideCanChangeTaxOptimum,
	setOverrideCanChangeAmlIdentification,
	setOverrideCanChangeContactAddress,
	setOverrideCanChangeElectronicContacts,
	setOverrideCanChangeIsEmployerContributing,
	setOverrideCanChengeInflationProgram,
	setOverrideCanChangeBeneficiaries,
	setOverrideIsClientIdentified,
	setOverrideIsBankIdIdentificationRequired,
	setOverrideIsAmlFulfilled,
	setOverrideHasAnyFailedChanges,
	setOverrideIsElectronicContactsChangeInProgress,
	setOverrideIsContactAddressChangeInProgress,
	resetContractOverrideFlag,
	resetContractOverrides,
	resetAllOverrides,
} = allowedChangesSlice.actions;

// --- Selectors ---

/** Get original + overridden data for a specific contract */
export const selectAllowedChanges = (state: RootState, contractId: string) => {
	const original = state.allowedChanges.data[contractId];
	if (!original) return undefined;

	const overrides = state.allowedChanges.overrides[contractId];
	if (!overrides) return original;

	return { ...original, ...overrides };
};

/** Get all contract IDs */
export const selectAllContractIds = createSelector(
	(state: RootState) => state.allowedChanges.data,
	(data) => Object.keys(data),
);

/** Get all contracts as array */
export const selectAllContracts = createSelector(
	(state: RootState) => state.allowedChanges.data,
	(data): AllowedChangesItem[] => Object.keys(data).map((contractId) => data[contractId]),
);

/** Get overrides for a specific contract */
export const selectContractOverrides = (state: RootState, contractId: string) =>
	state.allowedChanges.overrides[contractId] ?? {};

/** Check if data has been loaded */
export const selectIsLoaded = (state: RootState) => state.allowedChanges.loaded;

/** Check if data is currently loading */
export const selectIsLoading = (state: RootState) => state.allowedChanges.loading;

/** Get fetch error message */
export const selectError = (state: RootState) => state.allowedChanges.error;

export default allowedChangesSlice.reducer;
